import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

function window(p: TownPropPalette, x: number, y: number, w: number, h: number): string {
  return `<g data-part="upper-window">
    <rect x="${x - 3}" y="${y - 3}" width="${w + 6}" height="${h + 7}" fill="${hex(p.wood)}" />
    <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${hex(p.glass)}" opacity="0.65" />
    <path d="M${x + w / 2} ${y}V${y + h} M${x} ${y + h * 0.48}H${x + w}"
      stroke="${hex(p.wood)}" stroke-width="2" />
    <path d="M${x + 2} ${y + h - 2}H${x + w - 2}" stroke="${hex(p.glassCore)}" opacity="0.65" />
    <path d="M${x - 5} ${y + h + 5}H${x + w + 5}" stroke="${hex(p.woodEdge)}" stroke-width="2" />
  </g>`;
}

function stoneBase(p: TownPropPalette, top: number, height: number): string {
  return `<g data-part="stone-base">
    <rect x="0" y="${top}" width="200" height="${height}" fill="${hex(p.stone)}" />
    <path d="M0 ${top + height / 2}H200 M26 ${top}V${top + height / 2} M60 ${top + height / 2}V${top + height}
      M101 ${top}V${top + height / 2} M144 ${top + height / 2}V${top + height} M181 ${top}V${top + height / 2}"
      fill="none" stroke="${hex(p.iron)}" stroke-width="1" opacity="0.55" />
  </g>`;
}

/** A 200×180 roofless frontage fitted below an actual canopy; no drawn stepping ledges. */
export function buildHarborShopfrontBody(p: TownPropPalette): string {
  const wall = hex(p.primary), shade = hex(p.shade), wood = hex(p.wood), trim = hex(p.woodEdge);
  return `<g data-part="shopfront-wall">
    <rect x="0" y="0" width="200" height="180" fill="${wall}" />
    <rect x="0" y="0" width="200" height="12" fill="${wood}" />
    <rect x="0" y="12" width="200" height="5" fill="${shade}" />
    <rect data-part="quiet-service-band" x="8" y="88" width="184" height="54" fill="${shade}" opacity="0.48" />
    <path d="M5 12V178 M195 12V178 M72 12V165 M128 12V165" fill="none" stroke="${wood}" stroke-width="5" />
    <path d="M9 17V168 M191 17V168 M77 19V162 M133 19V162" fill="none" stroke="${trim}" stroke-width="0.8" opacity="0.55" />
    <path d="M10 20L63 62 M137 62L190 20" fill="none" stroke="${wood}" stroke-width="3" />
    ${window(p, 18, 30, 40, 36)} ${window(p, 142, 30, 40, 36)}
    <path d="M82 166V36Q100 24 118 36V166Z" fill="${wood}" />
    <path d="M88 164V42Q100 34 112 42V164Z" fill="${hex(p.iron)}" />
    <path d="M100 45V160 M89 91H111" fill="none" stroke="${trim}" stroke-width="1" opacity="0.42" />
    <circle cx="107" cy="115" r="1.6" fill="${trim}" />
    ${stoneBase(p, 165, 15)}
    <g data-part="low-stock" fill="${wood}" stroke="${trim}" stroke-width="0.7">
      <path d="M16 165V149L21 145H47L53 149V165Z" />
      <path d="M150 165V150H184V165Z M151 156H183 M166 151V164" />
    </g>
    <path d="M25 145V137Q29 131 34 137V145 M38 145V139Q42 133 46 139V145"
      fill="${hex(p.stone)}" stroke="${wood}" stroke-width="1" />
  </g>`;
}

/** A 200×380 civic facade. Story divisions give the existing high roof a grounded building. */
export function buildLanternHallBody(p: TownPropPalette): string {
  const wall = hex(p.primary), shade = hex(p.shade), wood = hex(p.wood), trim = hex(p.woodEdge);
  return `<g data-part="lantern-hall-wall">
    <rect x="0" y="0" width="200" height="380" fill="${wall}" />
    <rect x="0" y="0" width="200" height="14" fill="${wood}" />
    <rect x="0" y="14" width="200" height="10" fill="${shade}" />
    <rect x="8" y="128" width="184" height="98" fill="${shade}" opacity="0.4" />
    <rect data-part="quiet-service-band" x="8" y="258" width="184" height="100" fill="${shade}" opacity="0.56" />
    <path d="M6 18V368 M194 18V368 M72 16V225 M128 16V225" stroke="${wood}" stroke-width="6" fill="none" />
    <path d="M11 22V364 M189 22V364 M77 22V220 M133 22V220" stroke="${trim}" stroke-width="1" opacity="0.5" fill="none" />
    <path d="M9 116H191 M9 230H191" stroke="${wood}" stroke-width="8" />
    <path d="M11 113H189 M11 227H189" stroke="${trim}" stroke-width="1" opacity="0.65" />
    <path d="M14 27L64 100 M136 100L186 27 M14 139L64 211 M136 211L186 139"
      stroke="${wood}" stroke-width="3.5" fill="none" />
    ${window(p, 23, 38, 30, 48)} ${window(p, 147, 38, 30, 48)}
    ${window(p, 23, 151, 30, 44)} ${window(p, 147, 151, 30, 44)}
    <g data-part="hall-emblem">
      <path d="M88 40L100 33L112 40V77L100 88L88 77Z" fill="${wood}" />
      <path d="M94 47H106V68H94Z" fill="${hex(p.glass)}" opacity="0.68" />
      <path d="M100 39V78 M91 44H109 M91 72H109" stroke="${trim}" stroke-width="1.5" />
    </g>
    <path d="M68 365V274Q100 245 132 274V365Z" fill="${wood}" />
    <path d="M76 365V280Q100 259 124 280V365Z" fill="${hex(p.iron)}" />
    <path d="M81 365V284Q100 268 119 284V365Z" fill="${shade}" />
    <path d="M100 275V364 M87 288V362 M113 288V362 M82 310H118 M82 343H118"
      stroke="${wood}" stroke-width="2" fill="none" />
    <circle cx="96" cy="330" r="2" fill="${trim}" /><circle cx="104" cy="330" r="2" fill="${trim}" />
    ${stoneBase(p, 365, 15)}
  </g>`;
}
